import {
  useAuth,
  useClerk,
  useSignIn,
  useUser,
} from '@clerk/react';

import {
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

const API_URL =
  process.env.REACT_APP_API_URL ||
  'https://api.horizontl.io';

const isLocalMarketingHost =
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1' ||
  /^192\.168\./.test(window.location.hostname) ||
  /^10\./.test(window.location.hostname) ||
  /^172\.(1[6-9]|2\d|3[01])\./.test(
    window.location.hostname,
  );

const PRICING_URL = isLocalMarketingHost
  ? `http://${window.location.hostname}:8000/pricing/#plans`
  : 'https://horizontl.io/pricing/#plans';

const MARKETING_HOME_URL = isLocalMarketingHost
  ? `http://${window.location.hostname}:8000/`
  : 'https://horizontl.io/';

function BrandHeader({ marginBottom = '16px' }) {
  return (
    <a
      href={MARKETING_HOME_URL}
      aria-label="Go to Horizontl homepage"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        marginBottom,
        color: '#014035',
        fontSize: '22px',
        fontWeight: '800',
        letterSpacing: '-0.04em',
        textDecoration: 'none',
      }}
    >
      <img
        src="/favicon.png"
        alt=""
        width="34"
        height="34"
        style={{ objectFit: 'contain' }}
      />
      <span>Horizontl</span>
    </a>
  );
}

function Login() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { isLoaded, isSignedIn, user } = useUser();
  const { getToken } = useAuth();
  const { signOut } = useClerk();
  const { signIn } = useSignIn();

  const checkoutSuccess =
    searchParams.get('checkout') === 'success';

  const sessionId = searchParams.get('session_id');
  const isVerificationPage = searchParams.get('verify') === '1';
  const verificationStatus =
    signIn?.emailLink?.verification?.status ||
    searchParams.get('__clerk_status');
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [signInLoading, setSignInLoading] = useState(false);
  const [signInError, setSignInError] = useState(null);

  const [provisioning, setProvisioning] = useState(
    checkoutSuccess && !isVerificationPage,
  );

  const [provisioned, setProvisioned] = useState(
    !checkoutSuccess || isVerificationPage,
  );

  const [accountStatus, setAccountStatus] =
    useState('idle');

  const [accountError, setAccountError] =
    useState(null);

  const [setupError, setSetupError] =
    useState(null);

  const [changingAccount, setChangingAccount] =
    useState(false);

  const emailAddress =
    user?.primaryEmailAddress?.emailAddress ||
    user?.emailAddresses?.[0]?.emailAddress ||
    '';

  const portalDestination = checkoutSuccess
    ? `/home?checkout=success${
        sessionId
          ? `&session_id=${encodeURIComponent(sessionId)}`
          : ''
      }`
    : '/home';

  const finalizeCheckout = useCallback(async () => {
    if (!checkoutSuccess) {
      setProvisioned(true);
      return;
    }

    if (!sessionId) {
      setProvisioning(false);
      setSetupError(
        'The Stripe Checkout Session ID is missing.',
      );
      return;
    }

    try {
      setProvisioning(true);
      setSetupError(null);

      const response = await fetch(
        `${API_URL}/api/finalize-checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            session_id: sessionId,
          }),
        },
      );

      let data = {};

      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (!response.ok) {
        throw new Error(
          data.detail ||
            'Workspace setup could not be completed.',
        );
      }

      if (
        ['checkout_pending', 'payment_pending'].includes(
          data.status,
        )
      ) {
        throw new Error(
          'Payment confirmation is still processing. Please retry.',
        );
      }

      if (data.status === 'cancelled') {
        throw new Error(
          data.reason ||
            'This subscription could not be provisioned.',
        );
      }

      setProvisioned(true);
    } catch (err) {
      console.error(
        'Checkout finalisation failed:',
        err,
      );

      setSetupError(
        err.message ||
          'Workspace setup could not be completed.',
      );
    } finally {
      setProvisioning(false);
    }
  }, [
    checkoutSuccess,
    sessionId,
  ]);

  useEffect(() => {
    finalizeCheckout();
  }, [finalizeCheckout]);

  /*
   * Confirm that the active Clerk identity is linked to a
   * provisioned Horizontl user and organisation.
   */
  useEffect(() => {
    if (
      !isLoaded ||
      !isSignedIn ||
      isVerificationPage ||
      !provisioned
    ) {
      return;
    }

    let cancelled = false;

    const checkAccount = async () => {
      try {
        setAccountStatus('checking');
        setAccountError(null);

        const token = await getToken();

        if (!token) {
          throw new Error('active_session_missing');
        }

        const response = await fetch(
          `${API_URL}/api/me`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        let data = {};

        try {
          data = await response.json();
        } catch {
          data = {};
        }

        if (cancelled) {
          return;
        }

        if (response.ok) {
          setAccountStatus('ready');
          return;
        }

        if (
          response.status === 403 ||
          data.detail === 'account_not_provisioned' ||
          data.detail === 'organisation_not_found'
        ) {
          setAccountStatus('unprovisioned');
          return;
        }

        if (response.status === 401) {
          setAccountStatus('invalid_session');
          return;
        }

        throw new Error(
          data.detail || 'account_check_failed',
        );
      } catch (err) {
        if (cancelled) {
          return;
        }

        console.error(
          'Account access check failed:',
          err,
        );

        setAccountStatus('error');

        setAccountError(
          'We could not check your workspace access. Please try again.',
        );
      }
    };

    checkAccount();

    return () => {
      cancelled = true;
    };
  }, [
    isLoaded,
    isSignedIn,
    isVerificationPage,
    provisioned,
    getToken,
  ]);

  useEffect(() => {
    if (accountStatus === 'ready' && !isVerificationPage) {
      navigate(portalDestination, {
        replace: true,
      });
    }
  }, [
    accountStatus,
    isVerificationPage,
    navigate,
    portalDestination,
  ]);

  const handleStartSignup = () => {
    window.location.assign(PRICING_URL);
  };

  const readableClerkError = (error) => {
    const clerkError = error?.errors?.[0] || error;
    const code = clerkError?.code;

    if (
      code === 'form_identifier_not_found' ||
      code === 'identifier_not_found'
    ) {
      return 'We could not find a Horizontl account for this email.';
    }

    return (
      clerkError?.longMessage ||
      clerkError?.message ||
      'We could not send your secure sign-in link. Please try again.'
    );
  };

  const handleSignInSubmit = async (event) => {
    event.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail) {
      setSignInError('Please enter your email address.');
      return;
    }

    if (!signIn) {
      setSignInError('Authentication is still loading. Please try again.');
      return;
    }

    try {
      setSignInLoading(true);
      setSignInError(null);

      const verificationParams = new URLSearchParams(window.location.search);
      verificationParams.set('verify', '1');
      const verificationUrl =
        `${window.location.origin}/login?${verificationParams.toString()}`;

      const { error: sendError } = await signIn.emailLink.sendLink({
        emailAddress: normalizedEmail,
        verificationUrl,
      });
      if (sendError) throw sendError;

      setEmailSent(true);
      setSignInLoading(false);

      const { error: verificationError } =
        await signIn.emailLink.waitForVerification();
      if (verificationError) throw verificationError;

      if (signIn.status !== 'complete') {
        console.error('Sign-in incomplete after email verification:', {
          status: signIn.status,
          firstFactorVerification: signIn.firstFactorVerification,
          secondFactorVerification: signIn.secondFactorVerification,
          supportedSecondFactors: signIn.supportedSecondFactors,
        });

        throw new Error(
          'Your email was verified, but sign-in could not be completed.',
        );
      }

      const { error: finalizeError } = await signIn.finalize({
        navigate: ({ session, decorateUrl }) => {
          if (session?.currentTask) {
            setSignInError(
              'Your account requires another authentication step.',
            );
            return;
          }

          const params = new URLSearchParams(window.location.search);
          params.delete('verify');
          const query = params.toString();
          const destination = decorateUrl(`/login${query ? `?${query}` : ''}`);
          window.location.replace(destination);
        },
      });
      if (finalizeError) throw finalizeError;
    } catch (error) {
      console.error('Email-link sign-in failed:', error);
      setSignInLoading(false);
      setSignInError(readableClerkError(error));
    }
  };

  const handleRestartSignIn = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete('verify');
    params.delete('__clerk_status');
    params.delete('__clerk_created_session');
    const query = params.toString();
    window.location.replace(`/login${query ? `?${query}` : ''}`);
  };

  const handleContinue = () => {
    navigate(portalDestination, {
      replace: true,
    });
  };

  const handleSignOut = async () => {
    try {
      setChangingAccount(true);
      setAccountError(null);

      await signOut();

      setAccountStatus('idle');
      setChangingAccount(false);

      navigate('/login', {
        replace: true,
      });
    } catch (err) {
      console.error('Sign out failed:', err);

      setChangingAccount(false);

      setAccountError(
        'We could not sign you out. Please try again.',
      );
    }
  };

  const handleSwitchAccount = async () => {
    await handleSignOut();
  };

  const pageStyle = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    width: '100%',
    maxWidth: '100vw',
    padding: '32px 24px',
    overflowX: 'hidden',
    backgroundColor: '#F7FBF9',
    backgroundImage:
      'radial-gradient(circle at 12% 12%, rgba(7, 193, 148, 0.14), transparent 30%), radial-gradient(circle at 88% 88%, rgba(228, 212, 92, 0.18), transparent 28%)',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Inter", sans-serif',
    boxSizing: 'border-box',
  };

  const cardStyle = {
    width: '100%',
    maxWidth: '480px',
    margin: '0 auto',
    padding: '42px',
    border: '1px solid #D9E5E1',
    borderRadius: '24px',
    backgroundColor: '#FFFFFF',
    boxSizing: 'border-box',
    textAlign: 'center',
  };

  const primaryButtonStyle = {
    width: '100%',
    marginTop: '18px',
    padding: '14px',
    border: 'none',
    borderRadius: '999px',
    backgroundColor: '#07C194',
    color: '#014035',
    fontSize: '14px',
    fontWeight: '800',
    cursor: 'pointer',
  };

  const secondaryButtonStyle = {
    width: '100%',
    marginTop: '10px',
    padding: '11px',
    border: '1px solid #014035',
    borderRadius: '999px',
    backgroundColor: '#FFFFFF',
    color: '#014035',
    fontSize: '14px',
    fontWeight: '700',
    cursor: 'pointer',
  };

  const textButtonStyle = {
    marginTop: '14px',
    border: 'none',
    backgroundColor: 'transparent',
    color: '#5A6070',
    fontSize: '13px',
    textDecoration: 'underline',
    cursor: 'pointer',
  };

  if (!isLoaded || provisioning) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <BrandHeader />

          <div
            style={{
              color: '#5A6070',
              fontSize: '14px',
            }}
          >
            {provisioning
              ? 'Payment successful. Setting up your workspace...'
              : 'Checking your session...'}
          </div>
        </div>
      </div>
    );
  }

  if (setupError) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <BrandHeader marginBottom="12px" />

          <h2
            style={{
              margin: '0 0 10px',
              color: '#1A1D27',
            }}
          >
            Workspace setup needs attention
          </h2>

          <p
            style={{
              color: '#C94A2A',
              fontSize: '14px',
              lineHeight: '1.5',
            }}
          >
            ⚠️ {setupError}
          </p>

          <button
            type="button"
            style={primaryButtonStyle}
            onClick={finalizeCheckout}
          >
            Retry setup
          </button>
        </div>
      </div>
    );
  }

  if (isVerificationPage) {
    const verificationFailed = [
      'failed',
      'expired',
      'client_mismatch',
    ].includes(verificationStatus);
    const verificationSucceeded = verificationStatus === 'verified';

    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <BrandHeader marginBottom="20px" />
          <h1 style={{ margin: '0 0 10px', color: '#061D18', fontSize: '30px' }}>
            {verificationSucceeded
              ? 'Email verified'
              : verificationFailed
                ? 'Sign-in link unavailable'
                : 'Verifying your email'}
          </h1>
          <p style={{ margin: '0', color: '#5B6D67', fontSize: '15px', lineHeight: '1.6' }}>
            {verificationSucceeded
              ? 'Return to your original sign-in tab. It will open your workspace automatically. You can close this tab.'
              : verificationStatus === 'expired'
                ? 'This sign-in link has expired. Return to the original tab and request a new link.'
                : verificationStatus === 'client_mismatch'
                  ? 'This link could not be matched to the sign-in attempt.'
                  : verificationStatus === 'failed'
                    ? 'This sign-in link could not be verified.'
                    : 'Please wait while we verify your secure sign-in link.'}
          </p>
          {verificationFailed && (
            <button type="button" style={secondaryButtonStyle} onClick={handleRestartSignIn}>
              Return to sign in
            </button>
          )}
        </div>
      </div>
    );
  }

  /* No active Clerk session. */
  if (!isSignedIn) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <BrandHeader marginBottom="24px" />

          <h1
            style={{
              margin: '0 0 10px',
              color: '#061D18',
              fontSize: '34px',
              lineHeight: '1.08',
              letterSpacing: '-0.055em',
              fontWeight: '650',
            }}
          >
            Sign in to Horizontl
          </h1>

          <p
            style={{
              margin: '0 0 28px',
              color: '#5B6D67',
              fontSize: '15px',
              lineHeight: '1.6',
            }}
          >
            Access your technical support workspace.
          </p>

          {emailSent ? (
            <>
              <div style={{ padding: '16px', border: '1px solid #BCE8DB', borderRadius: '14px', backgroundColor: '#EFFBF7', color: '#014035', fontSize: '14px', lineHeight: '1.6' }}>
                <strong>Check your email</strong><br />
                We sent a secure sign-in link to:<br />
                <strong>{email.trim().toLowerCase()}</strong><br />
                Keep this tab open. It will continue automatically.
              </div>
              {signInError && (
                <div style={{ marginTop: '14px', color: '#C94A2A', fontSize: '13px' }}>
                  ⚠️ {signInError}
                </div>
              )}
              <button type="button" style={textButtonStyle} onClick={handleRestartSignIn}>
                Use a different email
              </button>
            </>
          ) : (
            <form onSubmit={handleSignInSubmit}>
              <label htmlFor="email" style={{ display: 'block', marginBottom: '7px', color: '#061D18', fontSize: '13px', fontWeight: '700', textAlign: 'left' }}>
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={signInLoading}
                placeholder="you@company.com"
                style={{ width: '100%', minHeight: '48px', padding: '0 14px', border: '1px solid #C9D8D3', borderRadius: '12px', color: '#061D18', fontSize: '15px', boxSizing: 'border-box', outlineColor: '#07C194' }}
              />
              {signInError && (
                <div style={{ marginTop: '10px', color: '#C94A2A', fontSize: '13px', textAlign: 'left' }}>
                  ⚠️ {signInError}
                </div>
              )}
              <button type="submit" style={primaryButtonStyle} disabled={signInLoading}>
                {signInLoading ? 'Sending secure link...' : 'Continue →'}
              </button>
            </form>
          )}

          <div
            style={{
              marginTop: '26px',
              paddingTop: '22px',
              borderTop: '1px solid #D9E5E1',
              color: '#5B6D67',
              fontSize: '14px',
            }}
          >
            New to Horizontl?{' '}
            <a
              href={PRICING_URL}
              style={{
                color: '#014035',
                fontWeight: '800',
                textDecoration: 'underline',
                textUnderlineOffset: '3px',
              }}
            >
              Choose a plan →
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (
    accountStatus === 'checking' ||
    accountStatus === 'idle'
  ) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <BrandHeader />

          <div
            style={{
              color: '#5A6070',
              fontSize: '14px',
            }}
          >
            Checking your workspace access...
          </div>
        </div>
      </div>
    );
  }

  if (accountStatus === 'ready') {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <BrandHeader marginBottom="12px" />

          <h2
            style={{
              margin: '0 0 8px',
              color: '#1A1D27',
            }}
          >
            You’re signed in
          </h2>

          <p
            style={{
              margin: '0',
              color: '#9094A0',
              fontSize: '13px',
            }}
          >
            Signed in as
          </p>

          <div
            style={{
              marginTop: '6px',
              color: '#1A1D27',
              fontSize: '14px',
              fontWeight: '600',
              wordBreak: 'break-word',
            }}
          >
            {emailAddress}
          </div>

          {accountError && (
            <div
              style={{
                marginTop: '16px',
                color: '#C94A2A',
                fontSize: '13px',
              }}
            >
              ⚠️ {accountError}
            </div>
          )}

          <button
            type="button"
            style={primaryButtonStyle}
            onClick={handleContinue}
          >
            Continue to workspace →
          </button>

          <button
            type="button"
            style={secondaryButtonStyle}
            onClick={handleSwitchAccount}
            disabled={changingAccount}
          >
            {changingAccount
              ? 'Signing out...'
              : 'Sign in with another account'}
          </button>

          <button
            type="button"
            style={textButtonStyle}
            onClick={handleSignOut}
            disabled={changingAccount}
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  /*
   * Clerk identity exists, but it is not linked to an
   * active Horizontl workspace.
   */
  if (
    accountStatus === 'unprovisioned' ||
    accountStatus === 'invalid_session'
  ) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <BrandHeader marginBottom="12px" />

          <h2
            style={{
              margin: '0 0 8px',
              color: '#1A1D27',
            }}
          >
            Complete your signup
          </h2>

          <p
            style={{
              margin: '0',
              color: '#5A6070',
              fontSize: '14px',
              lineHeight: '1.5',
            }}
          >
            Email verified. Choose a plan to complete signup.
          </p>

          <div
            style={{
              marginTop: '14px',
              padding: '10px',
              borderRadius: '8px',
              backgroundColor: '#F7F8FA',
              color: '#1A1D27',
              fontSize: '14px',
              fontWeight: '600',
              wordBreak: 'break-word',
            }}
          >
            {emailAddress}
          </div>

          {accountError && (
            <div
              style={{
                marginTop: '16px',
                color: '#C94A2A',
                fontSize: '13px',
              }}
            >
              ⚠️ {accountError}
            </div>
          )}

          <button
            type="button"
            style={primaryButtonStyle}
            onClick={handleStartSignup}
          >
            Choose a plan and continue →
          </button>

          <button
            type="button"
            style={secondaryButtonStyle}
            onClick={handleSwitchAccount}
            disabled={changingAccount}
          >
            {changingAccount
              ? 'Signing out...'
              : 'Sign in with another account'}
          </button>

          <button
            type="button"
            style={textButtonStyle}
            onClick={handleSignOut}
            disabled={changingAccount}
          >
            Sign out
          </button>

          <p
            style={{
              margin: '20px 0 0',
              color: '#9094A0',
              fontSize: '12px',
              lineHeight: '1.5',
            }}
          >
            If you already paid or received an invitation,
            contact{' '}
            <a
              href="mailto:hello@horizontl.io"
              style={{
                color: '#1D9E75',
              }}
            >
              hello@horizontl.io
            </a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div
          style={{
            color: '#C94A2A',
            fontSize: '14px',
            lineHeight: '1.5',
          }}
        >
          ⚠️{' '}
          {accountError ||
            'We could not check your workspace access.'}
        </div>

        <button
          type="button"
          style={secondaryButtonStyle}
          onClick={() => window.location.reload()}
        >
          Try again
        </button>

        <button
          type="button"
          style={textButtonStyle}
          onClick={handleSignOut}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

export default Login;